/**
 * Functions that implement simple animations.
 *
 * @module component/util/animation
 */

// Third-party imports
import Velocity from 'velocity-animate';
import 'velocity-animate/velocity.ui';

// Local imports
import * as domelement from '../util/domelement.js';

function do_slide(element, slide_type, duration) {
    Velocity(element, slide_type, { duration });
}

/**
 * Toggle a slide animation on an element.
 *
 * This will use a 'slide up' animation if the element is visible, and a 'slide
 * down' animation if the element is hidden.
 *
 * Note: this only depends on if the element is visible, it does not matter
 * whether the element is in the viewport or not.
 *
 * @param {object} element
 * A DOM element object that the slide animation should be toggled for.
 *
 * @param {number} [duration=250]
 * An integer that represents how long in milliseconds the scroll animation
 * should take.
 */
export function toggle_slide(element, duration = 250) {
    let slide_type = domelement.is_visible(element) ? 'slideUp' : 'slideDown';
    do_slide(element, slide_type, duration);
}

/**
 * Slide an element up until it is no longer displayed.
 *
 * This is typically used to hide an element that is currently visible.
 *
 * @param {object} element
 * A DOM element object that the slide animation should be applied to.
 *
 * @param {number} [duration=250]
 * An integer that represents how long in milliseconds the scroll animation
 * should take.
 */
export function slide_up(element, duration = 250) {
    do_slide(element, 'slideUp', duration);
}

/**
 * Slide an element down until it is displayed.
 *
 * This is typically used to display an element that is currently hidden.
 *
 * @param {object} element
 * A DOM element object that the slide animation should be applied to.
 *
 * @param {number} [duration=250]
 * An integer that represents how long in milliseconds the scroll animation
 * should take.
 */
export function slide_down(element, duration = 250) {
    do_slide(element, 'slideDown', duration);
}

/**
 * Scroll to the specified DOM element.
 *
 * @param {object} element
 * A DOM element object that should be scrolled to
 *
 * @param {number} [offset=0]
 * An integer that represents the offset in pixels relative to the top of the
 * specified element. A positive integer indicates a position below the top of
 * the specified element; a negative integer indicates a position above the top
 * of the specified element.
 *
 * @param {number} [duration=500]
 * An integer that represents how long in milliseconds the scroll animation
 * should take.
 */
export function scroll(element, offset = 0, duration = 500) {
    Velocity(element, 'scroll', { duration, offset, mobileHA: false });
}

/**
 * Scroll to the specified DOM element taking the header into account.
 *
 * This essentially is the same as scroll, except it will try and keep the
 * target element in view if the header is visible and in the viewport. It does
 * this by taking the height of the header, adding a buffer of 20 pixels to it,
 * and feeds the negative value of it as the offset parameter to the scroll
 * function.
 *
 * This is needed when using a mobile screen resolution as the header is always
 * visible and layered on top of the page.
 *
 * @param {Window} global
 * The global object (window in browser context)
 *
 * @param {object} element
 * A DOM element object that should be scrolled to
 *
 * @param {number} [offset=0]
 * An integer that represents the offset in pixels relative to the top of the
 * specified element. A positive integer indicates a position below the top of
 * the specified element; a negative integer indicates a position above the top
 * of the specified element.
 *
 * @param {number} [duration=500]
 * An integer that represents how long in milliseconds the scroll animation
 * should take.
 *
 */
export function scroll_with_header(
    global,
    element,
    offset = 0,
    duration = 500
) {
    let doc = global.document;
    let header = doc.querySelector('.siteHeader');
    let header_height =
        header &&
        domelement.is_visible(header) &&
        domelement.in_viewport(global, header)
            ? header.offsetHeight + 20
            : 20;
    header_height = -header_height + offset;

    scroll(element, header_height, duration);
}
